// src/pages/GalleryGroupPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ref, get } from "firebase/database";
import { db } from "../lib/firebase";
import "./GalleryGroupPage.css";
import { CONFIG } from "../config";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";
import { getNavSectionStyles } from "../utils/navSectionStyles";

/** ---- Config ---- */
const IMAGES_BASE = CONFIG.IMAGES_BASE;

/** ---- Helpers ---- */
function slug(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const asArray = (v) =>
  Array.isArray(v) ? v : v && typeof v === "object" ? Object.values(v) : [];

const imgUrl = (filename) =>
  filename ? `${IMAGES_BASE}/${encodeURIComponent(filename)}` : "";

const resolveStyleValue = (customValue, fallbackValue) =>
  customValue !== undefined &&
  customValue !== null &&
  customValue !== ""
    ? customValue
    : fallbackValue;

// truncate description (max 20 chars)
function truncate(text, max = 20) {
  if (!text) return "";
  const s = String(text).trim();
  if (s.length <= max) return s;
  return s.slice(0, max).trimEnd() + ".....";
}

export default function GalleryGroupPage() {
  const { groupSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);
  const [homepageCfg, setHomepageCfg] = useState(null);
  const [error, setError] = useState("");
  const isMobile = useIsMobile();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const [navSnap, homeSnap] = await Promise.all([
          get(ref(db, "navconfig/dropdowns")),
          get(ref(db, "homepageconfig")),
        ]);

        if (homeSnap.exists() && !cancelled) {
          setHomepageCfg(homeSnap.val() || null);
        }

        if (!navSnap.exists()) {
          if (!cancelled) {
            setError("Configuration not found.");
            setLoading(false);
          }
          return;
        }

        const dropdowns = asArray(navSnap.val());
        const match = dropdowns.find(
          (g) => g && g.name && slug(g.name) === groupSlug
        );

        if (!match) {
          if (!cancelled) {
            setError("No such category group found.");
            setLoading(false);
          }
          return;
        }

        if (!cancelled) {
          setGroup(match);
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError("Failed to load gallery.");
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [groupSlug]);

  // Build items with cover image + grid config coming from Firebase
  const items = useMemo(() => {
    if (!group) return [];
    const rawItems = asArray(group.items);
    return rawItems
      .filter((it) => it && it.label)
      .map((it) => {
        const images = asArray(it.images);
        const coverImg =
          images.find((im) => im && im.cover) || images[0] || null;

        return {
          id: it.id || slug(it.label),
          label: it.label,
          description: it.displayDescription || it.description || "",
          href: `/gallery/${slug(group.name)}/${slug(it.label)}`,
          coverSrc: coverImg ? imgUrl(coverImg.filename) : "",
          // read grid config from subcategory (with fallback defaults)
          mobileCols:
            it.mobileCols !== undefined && it.mobileCols !== null
              ? Number(it.mobileCols)
              : 2,
          desktopCols:
            it.desktopCols !== undefined && it.desktopCols !== null
              ? Number(it.desktopCols)
              : 4,
        };
      });
  }, [group]);

  // Decide how many cards per row using the FIRST item config
  const mobileCols =
    items.length > 0 && items[0].mobileCols > 0 ? items[0].mobileCols : 2;
  const desktopCols =
    items.length > 0 && items[0].desktopCols > 0 ? items[0].desktopCols : 4;

  const { fontStyles, sectionStyles } = useMemo(
    () => getNavSectionStyles(homepageCfg, group),
    [homepageCfg, group]
  );
  const backgroundColor = homepageCfg?.backgroundColor || "#ffffff";
  const sizeOverrides = sectionStyles?.fontSizes || {};

  const sectionTitleFont = fontStyles?.sectionTitle || fontStyles?.heading || {};
  const sectionDescFont =
    fontStyles?.sectionDescription || fontStyles?.subHeading || {};
  const cardTitleFont = fontStyles?.content || fontStyles?.subHeading || {};
  const cardTextFont = fontStyles?.content || {};
  const useGroupStyleConfig = !!group?.useSectionStyleConfig;
  const applyGroupCardStyleToAllCards =
    !!group?.applyCardStyleToAllCards || useGroupStyleConfig;

  const titleStyle = {
    fontFamily: sectionTitleFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      resolveStyleValue(
        useGroupStyleConfig ? group?.titleFontSize : undefined,
        sizeOverrides.heading || sectionTitleFont?.fontSize
      ) || undefined,
      isMobile
    ),
    fontWeight: sectionTitleFont?.fontWeight || 700,
    lineHeight: sectionTitleFont?.lineHeight || "1.2",
    color:
      resolveStyleValue(
        useGroupStyleConfig ? group?.titleColor : undefined,
        sectionStyles?.headingColor || sectionTitleFont?.color
      ) || undefined,
  };

  const subtitleStyle = {
    fontFamily: sectionDescFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      resolveStyleValue(
        useGroupStyleConfig ? group?.descriptionFontSize : undefined,
        sizeOverrides.subHeading || sectionDescFont?.fontSize
      ) || undefined,
      isMobile
    ),
    fontWeight: sectionDescFont?.fontWeight || 400,
    lineHeight: sectionDescFont?.lineHeight || "1.6",
    color:
      resolveStyleValue(
        useGroupStyleConfig ? group?.descriptionColor : undefined,
        sectionStyles?.subHeadingColor || sectionDescFont?.color
      ) || undefined,
  };

  const cardTitleStyle = {
    fontFamily: cardTitleFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      resolveStyleValue(
        applyGroupCardStyleToAllCards ? group?.cardTitleFontSize : undefined,
        sizeOverrides.content || cardTitleFont?.fontSize
      ) || undefined,
      isMobile,
      3
    ),
    fontWeight: cardTitleFont?.fontWeight || 600,
    lineHeight: cardTitleFont?.lineHeight || "1.3",
    color:
      resolveStyleValue(
        applyGroupCardStyleToAllCards ? group?.cardTitleColor : undefined,
        sectionStyles?.contentColor || cardTitleFont?.color
      ) || undefined,
  };

  const cardTextStyle = {
    fontFamily: cardTextFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      resolveStyleValue(
        applyGroupCardStyleToAllCards
          ? group?.cardDescriptionFontSize
          : undefined,
        sizeOverrides.content || cardTextFont?.fontSize
      ) || undefined,
      isMobile,
      4
    ),
    fontWeight: cardTextFont?.fontWeight || 400,
    lineHeight: cardTextFont?.lineHeight || "1.6",
    color:
      resolveStyleValue(
        applyGroupCardStyleToAllCards ? group?.cardDescriptionColor : undefined,
        sectionStyles?.contentColor || cardTextFont?.color
      ) || undefined,
  };

  if (loading) {
    return (
      <div className="ggp-page" style={{ background: backgroundColor }}>
        <div className="ggp-wrap">
          <p className="ggp-status">Loading gallery...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ggp-page" style={{ background: backgroundColor }}>
        <div className="ggp-wrap">
          <p className="ggp-status ggp-error">{error}</p>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="ggp-page" style={{ background: backgroundColor }}>
        <div className="ggp-wrap">
          <p className="ggp-status ggp-error">No data available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ggp-page" style={{ background: backgroundColor }}>
      <div
        className="ggp-wrap"
        style={{
          fontFamily: fontStyles?.content?.fontFamily || undefined,
          color: sectionStyles?.contentColor || fontStyles?.content?.color || undefined,
        }}
      >
        <div className="ggp-hero">
          <header className="ggp-header">
            <p className="ggp-kicker">Gallery</p>
            <h1 className="ggp-title" style={titleStyle}>
              {group.displayTitle || group.name}
            </h1>
            {(group.displayDescription || group.description) && (
              <p className="ggp-subtitle" style={subtitleStyle}>
                {group.displayDescription || group.description}
              </p>
            )}
          </header>
        </div>

        {/* Pass grid config as CSS variables */}
        <section
          className="ggp-grid"
          style={{
            "--ggp-mobile-cols": String(mobileCols),
            "--ggp-desktop-cols": String(desktopCols),
          }}
        >
          {items.map((it) => (
            <Link key={it.id} to={it.href} className="ggp-card">
              <div className="ggp-image-wrap">
                {it.coverSrc ? (
                  <img src={it.coverSrc} alt={it.label} className="ggp-image" />
                ) : (
                  <div className="ggp-image-placeholder">No Image</div>
                )}
              </div>
              <div className="ggp-card-body">
                <h2 className="ggp-card-title" style={cardTitleStyle}>
                  {it.label}
                </h2>

                {it.description && (
                  <p className="ggp-card-text" style={cardTextStyle}>
                    {truncate(it.description, 20)}
                  </p>
                )}
              </div>
            </Link>
          ))}

          {items.length === 0 && (
            <p className="ggp-status">No categories configured yet.</p>
          )}
        </section>
      </div>
    </div>
  );
}
